import React, { ImgHTMLAttributes } from 'react';
import { Icon } from '@iconify/react';
import { cloneElement, isValidElement, ReactNode, useState } from 'react';
import LoadingPlaceholder from '../../../../../../muiComponents/LoadingPlaceholder';
import '../../../../stylesheets/gallery.scss';
import GalleryLightbox from './GalleryLightbox';

const DEFAULT_INITIAL_VISIBLE_COUNT = 5;
const DEFAULT_LOAD_MORE_COUNT = 8;

// Small galleries get a fixed layout instead of the usual featured + dense grid.
const getLayoutClassName = (index: number, total: number) => {
    if (total === 1) {
        return 'gallery__item--solo';
    }
    if (total === 2) {
        return 'gallery__item--half';
    }
    if (total === 3) {
        return index === 0 ? 'gallery__item--featured' : 'gallery__item--wide';
    }
    return index === 0 ? 'gallery__item--featured' : '';
};

interface GalleryItemState {
    index: number;
    isFeatured: boolean;
}

// Accepts any next/image prop (className, width, height, style, onError, ...)
export type GalleryImage = ImgHTMLAttributes<HTMLImageElement>;

interface GalleryProps<T extends GalleryImage> {
    title: ReactNode;
    titleId?: string;
    subtitle?: ReactNode;
    items: T[];
    renderItem?: (item: T, state: GalleryItemState) => ReactNode;
    showMoreLabel: string;
    initialVisibleCount?: number;
    loadMoreCount?: number;
}

interface GalleryImageComponentProps extends GalleryImage {
    isFeatured: boolean;
}

const GalleryImage = ({
    isFeatured,
    alt,
    className,
    onLoad,
    style,
    ...rest
}: GalleryImageComponentProps) => {
    const [isLoaded, setIsLoaded] = useState(false);

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {!isLoaded && (
                <LoadingPlaceholder
                    variant='rectangular'
                    animation='wave'
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        transform: 'none',
                    }}
                />
            )}
            <img
                sizes={
                    isFeatured
                        ? '(max-width: 768px) 100vw, 50vw'
                        : '(max-width: 768px) 50vw, 25vw'
                }
                {...rest}
                alt={alt}
                onLoad={event => {
                    setIsLoaded(true);
                    onLoad?.(event);
                }}
                style={{
                    ...style,
                    opacity: isLoaded ? 1 : 0,
                    transition: 'opacity 180ms ease-in',
                }}
                className={`gallery__image${className ? ` ${className}` : ''}`}
            />
        </div>
    );
};

const renderGalleryImage = <T extends GalleryImage>(
    item: T,
    { isFeatured }: GalleryItemState
) => <GalleryImage {...item} isFeatured={isFeatured} />;

const Gallery = <T extends GalleryImage>({
    title,
    titleId: providedTitleId,
    subtitle,
    items,
    renderItem = renderGalleryImage,
    showMoreLabel,
    initialVisibleCount = DEFAULT_INITIAL_VISIBLE_COUNT,
    loadMoreCount = DEFAULT_LOAD_MORE_COUNT,
}: GalleryProps<T>) => {
    const generatedTitleId = `gallery-title-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
    const resolvedTitleId = providedTitleId ?? generatedTitleId;
    const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const isTitleText = typeof title === 'string';

    const remainingCount = Math.max(items.length - visibleCount, 0);

    const renderTitle = () => {
        if (isTitleText) {
            return (
                <h2 id={resolvedTitleId} className='gallery__title'>
                    {title}
                </h2>
            );
        }

        if (isValidElement<{ id?: string }>(title)) {
            return cloneElement(title, {
                id: resolvedTitleId,
            });
        }

        return title;
    };

    const handleShowMore = () => {
        setVisibleCount(count => Math.min(count + loadMoreCount, items.length));
    };

    return (
        <section className='gallery' aria-labelledby={resolvedTitleId}>
            <div className='gallery__header'>
                {renderTitle()}
                {subtitle && (
                    <div className='gallery__subtitle'>{subtitle}</div>
                )}
            </div>

            {/* All items stay in the markup for crawlers/AI agents;
          items beyond visibleCount are only visually hidden until expanded. */}
            <ul className='gallery__grid'>
                {items.map((item, index) => {
                    const isFeatured = index === 0;
                    const isHidden = index >= visibleCount;
                    const isLastVisible =
                        index === visibleCount - 1 && remainingCount > 0;
                    const layoutClassName = getLayoutClassName(
                        index,
                        items.length
                    );
                    const key = typeof item.src === 'string' ? item.src : index;

                    return (
                        <li
                            key={key}
                            className={`gallery__item${
                                layoutClassName ? ` ${layoutClassName}` : ''
                            }${isHidden ? ' gallery__item--hidden' : ''}`}
                        >
                            <div className='gallery__item-wrapper'>
                                {isLastVisible ? (
                                    <>
                                        {renderItem(item, {
                                            index,
                                            isFeatured,
                                        })}
                                        <button
                                            type='button'
                                            className='gallery__overlay'
                                            onClick={handleShowMore}
                                            aria-label={`${showMoreLabel} (+${remainingCount})`}
                                        >
                                            <Icon
                                                icon='mdi:image-multiple-outline'
                                                className='gallery__overlay-icon'
                                                aria-hidden='true'
                                            />
                                            <span
                                                className='gallery__overlay-count'
                                                aria-hidden='true'
                                            >
                                                +{remainingCount}
                                            </span>
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        type='button'
                                        className='gallery__item-trigger'
                                        onClick={() => setActiveIndex(index)}
                                        aria-label={item.alt}
                                    >
                                        {renderItem(item, {
                                            index,
                                            isFeatured,
                                        })}
                                    </button>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>

            <GalleryLightbox
                items={items}
                activeIndex={activeIndex}
                onClose={() => setActiveIndex(null)}
                onNavigate={setActiveIndex}
            />
        </section>
    );
};

export default Gallery;
